import axios, { AxiosError, AxiosRequestConfig, AxiosHeaders, AxiosResponse } from 'axios';
import { refreshTokenManager } from 'utils/refreshTokenManager';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  timeout: 30_000,
});

function setAuthHeader(config: AxiosRequestConfig, token?: string | null) {
  const h = config.headers;
  if (h && typeof (h as any).set === 'function') {
    const ah = h as AxiosHeaders;
    token ? ah.set('Authorization', `Bearer ${token}`) : ah.delete('Authorization');
    return;
  }
  token
    ? (config.headers = { ...(h as any), Authorization: `Bearer ${token}` })
    : (config.headers = { ...(h as any) });
}

// Request interceptor
api.interceptors.request.use(async config => {
  const { store } = await import('@store/store');
  const { getAccessToken } = await import('@store/auth/auth-selectors');
  const token = store.getState().impersonation.accessToken || getAccessToken(store.getState());
  const skipAuthHeader = (config.headers as any)?.['X-Skip-Auth'] === '1';

  if (skipAuthHeader) {
    const h = config.headers;
    if (h && typeof (h as any).delete === 'function') {
      (h as AxiosHeaders).delete('X-Skip-Auth');
      (h as AxiosHeaders).delete('Authorization');
    } else if (h && typeof h === 'object') {
      delete (h as any)['X-Skip-Auth'];
      delete (h as any).Authorization;
    }
    return config;
  }

  setAuthHeader(config, token);
  return config;
});

// Response interceptor
// src/utils/api.ts (твой файл с axios)
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!original) return Promise.reject(error);

    const status = error.response?.status;
    const url = typeof original.url === 'string' ? original.url : '';

    const isRefreshCall =
      typeof original.url === 'string' && original.url.includes('/auth/refresh');

    // НОВОЕ: флажок "не пытаться рефрешить"
    const skipRetryHeader = (original.headers as any)?.['X-Skip-Auth-Retry'] === '1';

    // НОВОЕ: защитный скоуп по префиксу (на всякий случай)
    const isSurveyInvite = url.includes('/api/v1/survey-invites/');

    if (
      status === 401 &&
      !original._retry &&
      !isRefreshCall &&
      !skipRetryHeader &&
      !isSurveyInvite
    ) {
      const { store } = await import('@store/store');
      if (store.getState().impersonation.accessToken) return Promise.reject(error);
      const { getAccessToken } = await import('@store/auth/auth-selectors');
      const token = getAccessToken(store.getState());
      if (!token) return Promise.reject(error);

      original._retry = true;
      try {
        await refreshTokenManager.refreshToken();
        return api(original);
      } catch (e) {
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  },
);
